import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Eye,
  Pencil,
  Trash2,
  Calendar,
  Printer,
  Lock,
  Unlock
} from "lucide-react";
import { printIntakeDetails } from "@/utils/printUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const allDaysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const IntakesTab = ({
  intakes,
  enrollments,
  getAuthHeaders,
  fetchAllData
}) => {
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [newIntake, setNewIntake] = useState({
    program_type: "",
    start_date: "",
    day_of_week: ""
  });

  const [editIntakeOpen, setEditIntakeOpen] = useState(false);
  const [editingIntake, setEditingIntake] = useState(null);
  const [editIntakeData, setEditIntakeData] = useState({
    program_type: "",
    start_date: "",
    day_of_week: "",
    max_puppies: 6
  });

  const [intakeDetailOpen, setIntakeDetailOpen] = useState(false);
  const [selectedIntakeDetail, setSelectedIntakeDetail] = useState(null);

  const handleCreateIntake = async () => {
    if (!newIntake.program_type || !newIntake.start_date || !newIntake.day_of_week) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/intakes`, newIntake, { headers });
      toast.success("Intake created successfully!");
      setIntakeOpen(false);
      setNewIntake({ program_type: "", start_date: "", day_of_week: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create intake");
    }
  };

  const handleUpdateIntake = async () => {
    if (!editingIntake) return;
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/intakes/${editingIntake.id}`, editIntakeData, { headers });
      toast.success("Intake updated successfully!");
      setEditIntakeOpen(false);
      setEditingIntake(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update intake");
    }
  };

  const handleDeleteIntake = async (intakeId, programName, startDate) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/intakes/${intakeId}`, { headers });
      toast.success(`Intake ${programName} (${startDate}) deleted successfully!`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete intake");
    }
  };

  const getIntakeAttendees = (intakeId) => {
    return enrollments.filter(e => e.intake_id === intakeId);
  };

  const handlePrintIntake = (intake) => {
    const attendees = getIntakeAttendees(intake.id);
    printIntakeDetails(intake, attendees);
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Dialog open={intakeOpen} onOpenChange={setIntakeOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-intake">
              <Plus className="w-4 h-4 mr-2" />
              Create Intake
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Intake</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Program</Label>
                <Select
                  value={newIntake.program_type}
                  onValueChange={(v) => setNewIntake(prev => ({ ...prev, program_type: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select program" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="puppy_foundations">Puppy Foundations</SelectItem>
                    <SelectItem value="puppy_development">Puppy Development</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Day</Label>
                <Select
                  value={newIntake.day_of_week}
                  onValueChange={(v) => setNewIntake(prev => ({ ...prev, day_of_week: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select day" /></SelectTrigger>
                  <SelectContent>
                    {allDaysOfWeek.map(day => (
                      <SelectItem key={day} value={day}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newIntake.start_date}
                  onChange={(e) => setNewIntake(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <Button onClick={handleCreateIntake} className="btn-primary w-full">
                Create Intake
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-premium p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-sand/50">
              <TableHead>Program</TableHead>
              <TableHead>Day</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Spots</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {intakes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-stone">
                  No intakes yet
                </TableCell>
              </TableRow>
            ) : (
              intakes.map((intake) => (
                <TableRow key={intake.id}>
                  <TableCell 
                    className="font-medium cursor-pointer hover:text-olive transition-colors"
                    onClick={() => {
                      setSelectedIntakeDetail(intake);
                      setIntakeDetailOpen(true);
                    }}
                  >
                    {intake.program_name}
                  </TableCell>
                  <TableCell>{intake.day_of_week}</TableCell>
                  <TableCell>{intake.start_date}</TableCell>
                  <TableCell>{intake.end_date}</TableCell>
                  <TableCell>
                    {getIntakeAttendees(intake.id).length}/{intake.max_puppies || 6}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 items-center">
                      <Badge className={
                        intake.manually_closed ? "bg-red-600" :
                        intake.status === "in_progress" ? "bg-olive" :
                        intake.status === "upcoming" ? "bg-terracotta" :
                        intake.status === "completed" ? "bg-charcoal" : "bg-sand text-charcoal"
                      }>
                        {intake.manually_closed ? "Fully Booked" : intake.status}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {/* Toggle Fully Booked Status */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        className={intake.manually_closed 
                          ? "text-green-600 border-green-600 hover:bg-green-50" 
                          : "text-orange-600 border-orange-600 hover:bg-orange-50"
                        }
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/intakes/${intake.id}/toggle-closed`, {}, { headers: getAuthHeaders() });
                            toast.success(intake.manually_closed 
                              ? `${intake.program_name} reopened for bookings` 
                              : `${intake.program_name} marked as fully booked`
                            );
                            fetchAllData();
                          } catch (err) {
                            toast.error("Failed to update intake status");
                          }
                        }}
                        title={intake.manually_closed ? "Reopen for bookings" : "Mark as fully booked"}
                        data-testid={`toggle-closed-${intake.id}`}
                      >
                        {intake.manually_closed ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedIntakeDetail(intake);
                          setIntakeDetailOpen(true);
                        }}
                        data-testid={`view-intake-${intake.id}`}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setEditingIntake(intake);
                          setEditIntakeData({
                            program_type: intake.program_type,
                            start_date: intake.start_date,
                            day_of_week: intake.day_of_week,
                            max_puppies: intake.max_puppies || 6
                          });
                          setEditIntakeOpen(true);
                        }}
                        data-testid={`edit-intake-${intake.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" data-testid={`delete-intake-${intake.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Intake</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete the <strong>{intake.program_name}</strong> intake starting {intake.start_date}?
                              <span className="block mt-2 text-red-600">
                                Warning: This will also delete all enrollments for this intake.
                              </span>
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => handleDeleteIntake(intake.id, intake.program_name, intake.start_date)}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Intake Dialog */}
      <Dialog open={editIntakeOpen} onOpenChange={setEditIntakeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Intake</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Program</Label>
              <Select
                value={editIntakeData.program_type}
                onValueChange={(v) => setEditIntakeData(prev => ({ ...prev, program_type: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="puppy_foundations">Puppy Foundations</SelectItem>
                  <SelectItem value="puppy_development">Puppy Development</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Day</Label>
              <Select
                value={editIntakeData.day_of_week}
                onValueChange={(v) => setEditIntakeData(prev => ({ ...prev, day_of_week: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allDaysOfWeek.map(day => (
                    <SelectItem key={day} value={day}>{day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={editIntakeData.start_date}
                onChange={(e) => setEditIntakeData(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Maximum Spots</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={editIntakeData.max_puppies}
                onChange={(e) => setEditIntakeData(prev => ({ ...prev, max_puppies: parseInt(e.target.value) || 6 }))}
              />
            </div>
            <Button onClick={handleUpdateIntake} className="btn-primary w-full">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Intake Detail Modal */}
      <Dialog open={intakeDetailOpen} onOpenChange={setIntakeDetailOpen}>
        <DialogContent className="max-w-2xl">
          {selectedIntakeDetail && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-olive" />
                    {selectedIntakeDetail.program_name}
                  </DialogTitle>
                  <Button size="sm" variant="outline" onClick={() => handlePrintIntake(selectedIntakeDetail)}>
                    <Printer className="w-4 h-4 mr-1" />
                    Print
                  </Button>
                </div>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                {/* Intake Info */}
                <div className="grid grid-cols-2 gap-4 bg-sand/30 p-4 rounded-lg">
                  <div>
                    <p className="text-stone text-sm">Start Date</p>
                    <p className="font-medium text-charcoal">{selectedIntakeDetail.start_date}</p>
                  </div>
                  <div>
                    <p className="text-stone text-sm">End Date</p>
                    <p className="font-medium text-charcoal">{selectedIntakeDetail.end_date}</p>
                  </div>
                  <div>
                    <p className="text-stone text-sm">Day</p>
                    <p className="font-medium text-charcoal">{selectedIntakeDetail.day_of_week}s</p>
                  </div>
                  <div>
                    <p className="text-stone text-sm">Capacity</p>
                    <p className="font-medium text-charcoal">
                      {getIntakeAttendees(selectedIntakeDetail.id).length}/{selectedIntakeDetail.max_puppies || 6} spots filled
                    </p>
                  </div>
                </div>

                {/* Attendees List */}
                <div>
                  <h4 className="font-medium text-charcoal mb-3">Enrolled Puppies ({getIntakeAttendees(selectedIntakeDetail.id).length})</h4>
                  {getIntakeAttendees(selectedIntakeDetail.id).length === 0 ? (
                    <p className="text-stone text-sm">No enrollments for this intake yet</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-stone">Puppy</th>
                          <th className="text-left py-2 text-stone">Owner</th>
                          <th className="text-left py-2 text-stone">Status</th>
                          <th className="text-left py-2 text-stone">Sessions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getIntakeAttendees(selectedIntakeDetail.id).map(enrollment => (
                          <tr key={enrollment.id} className="border-b border-olive/10">
                            <td className="py-2 text-charcoal">{enrollment.puppy_name}</td>
                            <td className="py-2 text-charcoal">{enrollment.owner_name || 'N/A'}</td>
                            <td className="py-2">
                              <Badge className={
                                enrollment.status === "confirmed" || enrollment.status === "in_progress" ? "bg-olive" :
                                enrollment.status === "completed" ? "bg-charcoal" :
                                enrollment.status === "pending" ? "bg-terracotta" : "bg-sand text-charcoal"
                              }>
                                {enrollment.status}
                              </Badge>
                            </td>
                            <td className="py-2 text-charcoal">{enrollment.sessions_completed || 0}/4</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Session Progress */}
                <div>
                  <h4 className="font-medium text-charcoal mb-3">Session Progress</h4>
                  <div className="space-y-2">
                    {getIntakeAttendees(selectedIntakeDetail.id).map(enrollment => (
                      <div key={enrollment.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-olive/10">
                        <span className="text-charcoal">{enrollment.puppy_name}</span>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            {[1, 2, 3, 4].map(session => (
                              <div 
                                key={session}
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                  session <= (enrollment.sessions_completed || 0) 
                                    ? "bg-olive text-white" 
                                    : "bg-sand text-stone"
                                }`}
                              >
                                {session}
                              </div>
                            ))}
                          </div>
                          <span className="text-sm text-stone ml-2">
                            {enrollment.sessions_completed || 0}/4 completed
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
